// /app/frontend/src/pages/risk/dpia/constants.js
import { 
  FileText, Users, Scale, Share2, Globe, 
  MessageSquare, Target, AlertTriangle, CheckCircle, UserCheck,
  Database
} from 'lucide-react';

// Tab Configuration
export const TABS = [
  { id: 1, label: 'Project Details', icon: FileText },
  { id: 2, label: 'Scope', icon: Users },
  { id: 3, label: 'Data', icon: Database },
  { id: 4, label: 'Lawful Basis', icon: Scale },
  { id: 5, label: 'Data Sharing', icon: Share2 },
  { id: 6, label: 'Transfers', icon: Globe },
  { id: 7, label: 'Consultation', icon: MessageSquare },
  { id: 8, label: 'Necessity', icon: Target },
  { id: 9, label: 'Risks', icon: AlertTriangle },
  { id: 10, label: 'Outcome', icon: CheckCircle },
  { id: 11, label: 'Approvals', icon: UserCheck },
];

// Categories of Individuals
export const INDIVIDUAL_CATEGORIES = [
  'Employees', 'Contractors', 'Job Applicants', 'Customers', 'Suppliers',
  'Business Contacts', 'Members', 'Patients', 'Students', 'Children',
  'Vulnerable Adults', 'General Public', 'Other'
];

// Lawful Basis Options
export const LAWFUL_BASIS_PERSONAL = [
  { value: 'consent', label: 'Consent', description: 'Individual has given clear consent.' },
  { value: 'contract', label: 'Contract Performance', description: 'Necessary for a contract.' },
  { value: 'legal_obligation', label: 'Legal Obligation', description: 'Required by law.' },
  { value: 'vital_interests', label: 'Vital Interests', description: 'Protect someone\'s life.' },
  { value: 'public_task', label: 'Public Interest', description: 'Public task or official authority.' },
  { value: 'legitimate_interests', label: 'Legitimate Interests', description: 'Legitimate business interest (requires LIA).' },
];

export const LAWFUL_BASIS_SPECIAL = [
  { value: 'explicit_consent', label: 'Explicit Consent', description: 'Explicit consent obtained.' },
  { value: 'employment_law', label: 'Employment/Social Security Law', description: 'Employment, social security, or social protection law.' },
  { value: 'vital_interests_incapable', label: 'Vital Interests', description: 'Where data subject is incapable of giving consent.' },
  { value: 'legitimate_activities', label: 'Legitimate Activities', description: 'Foundation, association, or non-profit body.' },
  { value: 'manifestly_public', label: 'Manifestly Made Public', description: 'Data made public by the data subject.' },
  { value: 'legal_claims', label: 'Legal Claims', description: 'Establishment, exercise, or defense of legal claims.' },
  { value: 'substantial_public_interest', label: 'Substantial Public Interest', description: 'On basis of law for substantial public interest.' },
  { value: 'health_purposes', label: 'Health/Social Care', description: 'Health or social care purposes.' },
  { value: 'public_health', label: 'Public Health', description: 'Reasons of public interest in public health.' },
  { value: 'archiving_research', label: 'Archiving/Research/Statistics', description: 'Archiving, scientific/historical research, or statistics.' },
];

// Review Frequency Options
export const REVIEW_FREQUENCIES = [
  '3 Months', '6 Months', '12 Months', '24 Months'
];

// Risk Level Colors and Labels (1-25 scale)
export const RISK_LEVELS = {
  low: { label: 'Low', color: 'bg-green-500', range: '1-4', action: 'Acceptable risk; monitor regularly' },
  moderate: { label: 'Moderate', color: 'bg-yellow-500', range: '5-9', action: 'Manage and monitor; implement controls as needed' },
  high: { label: 'High', color: 'bg-orange-500', range: '10-16', action: 'Take corrective action soon; assign owner and track' },
  extreme: { label: 'Extreme', color: 'bg-red-500', range: '17-25', action: 'Stop/change activity immediately; implement urgent mitigation' },
};

// Likelihood Scale (1-5)
export const LIKELIHOOD_SCALE = [
  { value: 1, label: 'Rare', description: 'Unlikely to happen' },
  { value: 2, label: 'Unlikely', description: 'Possible to happen' },
  { value: 3, label: 'Moderate', description: 'Likely to happen (even chance)' },
  { value: 4, label: 'Likely', description: 'Almost certain to happen' },
  { value: 5, label: 'Almost Certain', description: 'Expected to occur in most circumstances' },
];

// Impact Scale (1-5)
export const IMPACT_SCALE = [
  { value: 1, label: 'Insignificant', description: 'Minimal impact, no distress' },
  { value: 2, label: 'Minor', description: 'Slight impact, minor inconvenience' },
  { value: 3, label: 'Moderate', description: 'Noticeable impact, distress, or discomfort' },
  { value: 4, label: 'Major', description: 'Significant impact, serious harm, potential loss of rights' },
  { value: 5, label: 'Severe', description: 'Extreme, catastrophic impact, or permanent damage' },
];

export const getRiskLevel = (score) => {
  if (score >= 17) return 'extreme';
  if (score >= 10) return 'high';
  if (score >= 5) return 'moderate';
  return 'low';
};

export const getRiskBadgeColor = (level) => {
  const colors = {
    low: 'bg-green-100 text-green-800 border-green-300',
    moderate: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    extreme: 'bg-red-100 text-red-800 border-red-300',
  };
  return colors[level] || colors.low;
};

// Initial Form Data
export const getInitialFormData = (entityId) => ({
  entity_id: entityId || '',
  // Project Details
  dpia_reference: '',
  project_title: '',
  department: '',
  project_owner: '',
  project_lead_name: '',
  project_lead_position: '',
  person_completing_name: '',
  person_completing_position: '',
  contact_details: '',
  date_submitted: new Date().toISOString().split('T')[0],
  dpo_review_date: '',
  feedback_received: false,
  feedback_comments: '',
  // Scope
  project_description: '',
  categories_of_individuals: [],
  // Data Lifecycle
  data_collection_method: '',
  data_usage_method: '',
  data_storage_method: '',
  data_deletion_method: '',
  generates_new_data: false,
  // Data Items
  data_items: [],
  // Lawful Basis
  lawful_basis_personal: [],
  legitimate_interest_justification: '',
  consent_process_personal: '',
  involves_sensitive_data: false,
  lawful_basis_special: [],
  consent_process_special: '',
  // Data Sharing
  internal_sharing: false,
  internal_sharing_details: '',
  internal_sharing_reasons: '',
  internal_sharing_method: '',
  // Transfers
  third_parties: [],
  international_transfer: false,
  transfer_country: '',
  international_transfer_details: '',
  transfer_safeguards: '',
  // Consultation
  stakeholders_consulted: '',
  consultation_justification: '',
  // Necessity
  how_data_achieves_objectives: '',
  alternative_methods: '',
  can_use_anonymised: false,
  anonymised_explanation: '',
  function_creep_prevention: '',
  data_quality_measures: '',
  // Risks
  risks: [],
  // Outcome
  initial_project_risk: 0,
  project_risk_after_controls: 0,
  risk_reduction_points: 0,
  risk_reduction_percent: 0,
  dpo_advice_summary: '',
  dpo_consulted: false,
  dpo_advice_accepted: null,
  // Approvals
  approvers: [],
  review_frequency: '12 Months',
  review_owner: '',
  review_status: 'not_started',
  // Meta
  current_step: 1,
  status: 'draft',
});
